import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { compose } from 'ramda';
import { componentService } from 'js/services/component.service';
import { Button } from 'js/components/common/Button';
import { getConfirmDate, getPlayerTag, setConfirmDate, setTagStatus } from './store/actions';
import { Icon } from 'js/components/common/Icon';
import { useComponentTranslations } from 'js/hooks/component';
import { useFormatter } from 'js/hooks/useFormatter';
import { useConfirmationPopup } from './ConfirmationPopup';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { I18n } from 'js/components/common/I18n';
import { Control } from 'js/components/common/Fields/Control';
import DropDownIcon from 'js/components/common/Fields/Icons/DropDownIcon';

import * as hooks from './hooks';
import { handleVerifyClick } from './helpers';
import { withComponent } from 'js/components/hocs/withComponent';
import { withDynamicReducer, useDynamicReducerSelector } from './store';
import {
    JUMIO,
    BENEFICIAL_ACCOUNT_CONFIRMATION_TAG,
    BENEFICIAL_OWNER_CONFIRM,
    TAG_NAMES,
    ICON_TYPES
} from './constants';
import { additionalDocuments, primaryDocuments } from './documents';

import style from './Jumio.scss';

export const JumioBare = () => {
    const t = useComponentTranslations();
    const f = useFormatter();
    const [userData, setData] = useState({});
    const [posts, setPosts] = useState([]);
    const [tag, setTags] = useState([]);
    const [playerStatus, setPlayerStatus] = useState([]);
    const [isBocObjectApproved, setBOC] = useState([false]);
    const { uploadRequirement, haveCopyIDFax } = useDynamicReducerSelector();
    const status = hooks.useVerificationStatus({ uploadRequirement, haveCopyIDFax });
    const showConfirmationPopup = useConfirmationPopup();
    const { custom12: sk, custom13: gwg } = window.Playtech.fetchUserDetails();
    const verificationStatus = status;
    const title = t(`${verificationStatus === 'disabled' ? 'status.disabled' : 'title'}`);

    useEffect(() => {
        getTag();
        getBeneficialOwnerConfirmData();
        getPlayerStatus();
    }, []);

    useEffectOnce(() => {
        const fetchData = async () => {
            const result = await window.Playtech.API.user.getPlayerTags();

            setData(result.data);
        };

        fetchData();
    }, []);

    const getBeneficialOwnerConfirmData = async () => {
        const response = await getConfirmDate();
        setPosts(response.data?.userPaymentsInfoData);
    };

    const getPlayerStatus = async () => {
        const res = await window.Playtech.sendUserRegulationStatusRequest();
        setPlayerStatus(res.data.playerRegulationStatusHistory[0].regulationStatusId);
    };

    const getTag = async () => {
        const res = await getPlayerTag(BENEFICIAL_ACCOUNT_CONFIRMATION_TAG);
        setTags(res.data.tags);
    };

    const onAcceptBtn = (close) => {
        setConfirmDate()
            .then(() => {
                setTagStatus(BENEFICIAL_ACCOUNT_CONFIRMATION_TAG)
                .then(() => {
                    getPlayerTag(BENEFICIAL_ACCOUNT_CONFIRMATION_TAG)
                        .then((res) => {
                            if (res.data.tags[0].value === 'approved') {
                                setBOC([true]);
                            }
                            getBeneficialOwnerConfirmData();
                        })
                        .then(close)
                        .catch(() => console.warn('Jumio confirm error'));
                })
                .catch(() => console.warn('Jumio confirm error'));
            })
            .catch(({ data }) => data && console.log(data));
    };

    const onClick = useCallback(() => {
        showConfirmationPopup({
            isModal: false,
            content: 'playtech.mobile.system.beneficialOwner.confirmationText',
            title: 'playtech.mobile.system.beneficialOwner.confirmInfo',
            onAccept: onAcceptBtn
        });
    }, [showConfirmationPopup]);

    const getTagsUser = (tags) => {
        const userTags = TAG_NAMES.map(tagName => (tags.find(({ name }) => name === tagName) || {}).value);

        const [
            primaryTag,
            additionalTag,
            primaryVerificationStatus,
            additionalVerificationStatus
        ] = userTags;

        let documents = [];
        documents = primaryTag ? documents.concat(primaryDocuments) : documents;
        documents = additionalTag ? documents.concat(additionalDocuments) : documents;

        return {
            documents,
            primaryVerificationStatus,
            additionalVerificationStatus
        };
    };

    const useTagsUser = () => {
        if (userData && userData.tags) {
            return getTagsUser(userData.tags);
        }
    };

    const [documentType, setDocumentType] = useState();

    if (userData && userData.tags) {
        const {
            documents: documents,
            primaryVerificationStatus: primaryVerificationStatus,
            additionalVerificationStatus: additionalVerificationStatus
        } = useTagsUser();

        if (!documents.some(Boolean)) {
            return (
                <div className={style.container}>
                    <h3 className={style.title}>{t('status.disabled')}</h3>
                </div>
            );
        }

        const handleClick = () => handleVerifyClick(documentType);

        const options = documents.map(document => ({ ...document, label: t(document.label) }));

        return (
            <div className={`${style.container} cms-jumio`}>
                <ul className={style.statusListContainer}>
                    <li className={style.statusItem}>
                        <span className={style.nameContainer}>
                            <Icon type="accountStatus" className={style.iconMethods}/>
                            <span> {t('name.statusAccount')}</span>
                        </span>
                        <span className={style.statusResultContainer}>
                            <Icon type={playerStatus === 'VERIFIED' ? 'success' : 'attention'} className={style.iconMethods}/>
                            <span className={style.statusResultItem}>
                                {playerStatus === 'VERIFIED' ? t('playerStatus.verify') : t('playerStatus.provisional')}
                            </span>
                        </span>
                    </li>
                    <li className={style.statusItem}>
                        <span className={style.nameContainer}>
                            <Icon type="idPass" className={style.iconMethods}/>
                            <span> {t('name.idPass')}</span>
                        </span>
                            <span className={style.statusResultContainer}>
                            {primaryVerificationStatus ? (
                                <span>
                                    <Icon type={ICON_TYPES[primaryVerificationStatus]} className={style.iconMethods}/>
                                    <span className={`${style.statusResultItem} jumio-statusText`}>{t(`status.${primaryVerificationStatus}`)}</span>
                                </span>
                            ) : (
                                <span>{t('playerStatus.notVerify')}</span>
                            )}
                        </span>
                    </li>
                    <li className={style.statusItem}>
                        <span className={style.nameContainer}>
                             <Icon type="utilityBill" className={style.iconMethods}/>
                            <span>{t('name.utilityBIll')}</span>
                        </span>
                        <span className={style.statusResultContainer}>
                            {additionalVerificationStatus ? (
                                <span>
                                    <Icon type={ICON_TYPES[additionalVerificationStatus]} className={style.iconMethods}/>
                                    <span className={`${style.statusResultItem} jumio-statusText`}>{t(`status.${additionalVerificationStatus}`)}</span>
                                </span>
                            ) : (
                                <span>{t('playerStatus.notVerify')}</span>
                            )}
                        </span>
                    </li>
                    {gwg && <li className={style.statusItem}>
                        <span className={style.nameContainer}>
                            <Icon type="bankStatement" className={style.iconMethods}/>
                            <span> {t('name.bankStatement')}</span>
                        </span>
                        <span className={style.statusResultContainer}>
                            <span className={style.statusResultItem}>
                                {gwg === 'GWG_pending' && <Icon type={'pending'} className={style.iconMethods}/>}
                                {gwg === 'GWG_approved' && <Icon type={'success'} className={style.iconMethods}/>}
                                {gwg === 'GWG_pending' && t('playerStatus.pending')}
                                {gwg === 'GWG_approved' && t('playerStatus.approval')}
                                {gwg === 'GWG_optional' && t('playerStatus.optional')}
                            </span>
                        </span>
                    </li> }
                    {sk && <div>
                        <li className={style.statusItem}>
                            <span className={style.nameContainer}>
                                <Icon type="betreibungsregister" className={style.iconMethods}/>
                                <span> {t('name.betreibungsregister')}</span>
                            </span>
                                <span className={style.statusResultContainer}>
                                <span className={style.statusResultItem}>
                                    {sk === 'SK_approved' && <Icon type={'success'} className={style.iconMethods}/>}
                                    {sk === 'SK_approved' ? t('playerStatus.verify') : t('playerStatus.optional')}
                                </span>
                            </span>
                        </li>
                        <li className={style.statusItem}>
                            <span className={style.nameContainer}>
                                <Icon type="lohnausweis" className={style.iconMethods}/>
                                <span> {t('name.lohnausweis')}</span>
                            </span>
                            <span className={style.statusResultContainer}>
                                <span className={style.statusResultItem}>
                                    {sk === 'SK_approved' && <Icon type={'success'} className={style.iconMethods}/>}
                                    {sk === 'SK_approved' ? t('playerStatus.verify') : t('playerStatus.optional')}
                                </span>
                            </span>
                        </li>
                    </div>}
                    { tag.length > 0 && posts && <li className={style.statusItem}>
                        <span className={style.nameContainer}>
                            <Icon type="beneficialOwner" className={style.iconMethods}/>
                            <span> {t('name.beneficialOwner')}</span>
                        </span>
                        <div className={style.statusResultContainer}>
                            <div className={style.statusResultItem}>
                                <div>
                                    {tag[0].value === 'optional' && !isBocObjectApproved[0] ? (
                                        <Button onClick={onClick}
                                                className={style.beneficialButton}
                                                view="primary"
                                                size="medium">
                                            {t(BENEFICIAL_OWNER_CONFIRM)}
                                        </Button>
                                    ) : (
                                        <div>
                                            <I18n tKey={'playtech.mobile.system.beneficialOwner.confirmed'}/>
                                            <span>{`, ${f.date(posts.lastBeneficialOwnerConfirmDate).split('-')
                                                .reverse()
                                                .join('.')}`}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </li>}
                </ul>
                <h3 className={style.title}>{title}</h3>
                {verificationStatus !== 'disabled' && (
                    <Fragment>
                        <div className={style.controls}>
                            <Control
                                classNames={{ content: `${style.content}` }}
                            >
                                <select
                                    value={documentType}
                                    aria-label={t('label.documentType')}
                                    tabIndex={-1}
                                    onChange={e => setDocumentType(e.target.value)}
                                    className={style.select}
                                >
                                    {options.map(item => (
                                        <option
                                            key={item.value}
                                            value={item.value}
                                        >
                                            {item.label || item.value}
                                        </option>
                                    ))}
                                </select>
                                <DropDownIcon disabled={false} />
                            </Control>
                            <Button className={style.button} onClick={handleClick}>
                                {t('button')}
                            </Button>
                        </div>
                    </Fragment>
                )}
            </div>
        );

    }

    return <></>;
};

export const Jumio = compose(
    withComponent({ isForLoggedIn: true }),
    withDynamicReducer()
)(JumioBare);

componentService.registerPageComponents({ [JUMIO]: Jumio });
